import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  selectBoards,
  selectBoardIdsContainingVariant,
  ensureBoards,
  createBoard,
  addListingToBoard,
  removeListingFromBoard,
} from '../../ducks/boards.duck';
import { resolveVariantForDisplay } from '../../util/variantHelpers';

import { Modal } from '../../components';
import css from './AddToBoardModal.module.css';

// Which variant SKU to pin when saving: the one the buyer is viewing, else
// the first available, else the first overall (a discontinued default is fine
// — it still resolves). Returns null for parallel-SKU listings.
const pickVariantSku = (variants, preselectedSku) => {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  if (preselectedSku && variants.find(v => v.sku === preselectedSku)) return preselectedSku;
  const firstAvailable = variants.find(v => v.available !== false);
  return (firstAvailable || variants[0]).sku;
};

const AddToBoardModal = props => {
  const { listingId, listing, preselectedVariantSku, isOpen, onClose, onManageDisableScrolling } = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  // Variant context — which SKU to pin on the board entry. null for
  // parallel-SKU listings, so the board stores a bare listingId exactly as
  // before (buildAddMeta returns {} → no listingMeta written).
  const variants = listing?.attributes?.publicData?.variants;
  const skuToCommit = useMemo(
    () => pickVariantSku(variants, preselectedVariantSku),
    [variants, preselectedVariantSku]
  );
  const variantDisplay = skuToCommit ? resolveVariantForDisplay(variants, skuToCommit) : null;
  const buildAddMeta = () => (skuToCommit ? { variantSku: skuToCommit } : {});

  // Boards live in their own store now rather than on currentUser, so make
  // sure they are loaded before the list renders. Idempotent and a no-op when
  // signed out.
  useEffect(() => {
    if (isOpen) dispatch(ensureBoards());
  }, [isOpen, dispatch]);

  // Boards you can actually ADD TO. After Phase B the boards slice also holds
  // boards someone invited you to, and a 'client' (view-only) member is allowed
  // NO ops at all — OPS_BY_ROLE.client is an empty Set — so offering one here
  // produced a checkbox that 403s and whose error addListingToBoard swallows:
  // the tick simply never appeared and nothing said why. 'editor' members CAN
  // add, so they stay. A board with no role predates Phase B and is owned.
  const allBoards = useSelector(selectBoards);
  const boards = useMemo(
    () => allBoards.filter(b => !b.role || b.role === 'owner' || b.role === 'editor'),
    [allBoards]
  );
  // Per-variant membership: which boards already contain THIS colourway (not
  // just the product). For a flat listing (skuToCommit null) this is the
  // whole-listing membership, exactly as before.
  const boardIdsContaining = useSelector(state =>
    selectBoardIdsContainingVariant(state, listingId, skuToCommit)
  );
  const createInProgress = useSelector(s => s.boards.createInProgress);
  const addInProgress = useSelector(s => s.boards.addInProgress);

  const [newBoardName, setNewBoardName] = useState('');
  const [showNewBoardForm, setShowNewBoardForm] = useState(false);
  const [createError, setCreateError] = useState(null);

  const handleToggleBoard = boardId => {
    const isAlreadyIn = boardIdsContaining.includes(boardId);
    if (isAlreadyIn) {
      dispatch(removeListingFromBoard(boardId, listingId, skuToCommit));
    } else {
      dispatch(addListingToBoard(boardId, listingId, buildAddMeta()));
    }
  };

  const handleCreateBoard = e => {
    e.preventDefault();
    const name = newBoardName.trim();
    if (!name) return;

    setCreateError(null);
    dispatch(createBoard(name))
      .then(newBoardId => {
        dispatch(addListingToBoard(newBoardId, listingId, buildAddMeta()));
        setNewBoardName('');
        setShowNewBoardForm(false);
      })
      .catch(() => {
        setCreateError(intl.formatMessage({ id: 'AddToBoardModal.createError' }));
      });
  };

  const hasBoards = boards.length > 0;

  return (
    <Modal
      id="AddToBoardModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.root}>
        <h2 className={css.title}>
          <FormattedMessage id="AddToBoardModal.title" />
        </h2>

        {variantDisplay ? (
          <p
            className={
              variantDisplay.isDiscontinued ? css.variantStripDiscontinued : css.variantStrip
            }
          >
            <FormattedMessage
              id={
                variantDisplay.isDiscontinued
                  ? 'AddToBoardModal.savingVariantDiscontinued'
                  : 'AddToBoardModal.savingVariant'
              }
              values={{ label: variantDisplay.label }}
            />
          </p>
        ) : null}

        {hasBoards ? (
          <ul className={css.boardList}>
            {boards.map(board => {
              const isChecked = boardIdsContaining.includes(board.id);
              return (
                <li key={board.id} className={css.boardItem}>
                  <label className={css.boardLabel}>
                    <input
                      type="checkbox"
                      className={css.checkbox}
                      checked={isChecked}
                      onChange={() => handleToggleBoard(board.id)}
                      disabled={addInProgress}
                    />
                    <span className={css.boardName}>{board.name}</span>
                    <span className={css.boardCount}>
                      <FormattedMessage
                        id="AddToBoardModal.itemCount"
                        values={{ count: board.listingIds.length }}
                      />
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={css.emptyText}>
            <FormattedMessage id="AddToBoardModal.noBoards" />
          </p>
        )}

        {showNewBoardForm ? (
          <form className={css.newBoardForm} onSubmit={handleCreateBoard}>
            <input
              className={css.newBoardInput}
              type="text"
              value={newBoardName}
              onChange={e => setNewBoardName(e.target.value)}
              placeholder={intl.formatMessage({ id: 'AddToBoardModal.newBoardPlaceholder' })}
              autoFocus
              maxLength={80}
            />
            {createError && <p className={css.errorText}>{createError}</p>}
            <div className={css.newBoardActions}>
              <button
                type="button"
                className={css.cancelButton}
                onClick={() => {
                  setShowNewBoardForm(false);
                  setNewBoardName('');
                  setCreateError(null);
                }}
              >
                <FormattedMessage id="AddToBoardModal.cancel" />
              </button>
              <button
                type="submit"
                className={css.createButton}
                disabled={!newBoardName.trim() || createInProgress}
              >
                {createInProgress ? (
                  <FormattedMessage id="AddToBoardModal.creating" />
                ) : (
                  <FormattedMessage id="AddToBoardModal.createBoard" />
                )}
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            className={css.addNewButton}
            onClick={() => setShowNewBoardForm(true)}
          >
            <span className={css.plusIcon}>+</span>
            <FormattedMessage id="AddToBoardModal.newBoard" />
          </button>
        )}

        {/* Primary confirm at the bottom. Board membership already persists on
            each checkbox toggle, so this simply dismisses the modal — a clearer
            "I'm done" affordance than the top-right Close alone. */}
        <div className={css.footer}>
          <button type="button" className={css.doneButton} onClick={onClose}>
            <FormattedMessage id="AddToBoardModal.done" defaultMessage="Save" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddToBoardModal;
